import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import PremiumSelect from '../components/PremiumSelect';
import PageContainer from '../components/ui/PageContainer';
import StatusChip from '../components/ui/StatusChip';
import { User, PersonalDevelopmentGoal, PersonalDevelopmentMaterial, AlertState } from '../types';
import {
  PERSONAL_DEVELOPMENT_COMPETENCIES,
  isPersonalDevelopmentCompetency,
} from '../constants/personalDevelopmentCompetencies';

interface GoalFormState {
  competencyTitle: string;
  activityBook: string;
  activityMovie: string;
  activityTask: string;
  startDate: string;
  plannedEndDate: string;
  actualEndDate: string;
  developed: boolean;
}

const emptyForm = (): GoalFormState => ({
  competencyTitle: '',
  activityBook: '',
  activityMovie: '',
  activityTask: '',
  startDate: '',
  plannedEndDate: '',
  actualEndDate: '',
  developed: false,
});

/** Arquivo estático em `public/MATERIAL-FGCE.pdf` — mesma hierarquia dos enviados. */
const MATERIAL_FGCE_PUBLIC_URL = `${process.env.PUBLIC_URL ?? ''}/MATERIAL-FGCE.pdf`;
const PUBLIC_FGCE_ID = '__public_fgce__';
const FGCE_ORIGINAL_NAME = 'MATERIAL-FGCE.pdf';

const isFgceMaterial = (m: PersonalDevelopmentMaterial): boolean =>
  m.id === PUBLIC_FGCE_ID ||
  m.originalName === FGCE_ORIGINAL_NAME ||
  m.fileKey === 'static:MATERIAL-FGCE.pdf';

const publicFgceMaterial = (): PersonalDevelopmentMaterial => ({
  id: PUBLIC_FGCE_ID,
  title: 'Material FGCE',
  originalName: FGCE_ORIGINAL_NAME,
  mimeType: 'application/pdf',
  sortOrder: 0,
  fileKey: 'static:MATERIAL-FGCE.pdf',
  createdAt: '',
  updatedAt: '',
});

const toInputDate = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.slice(0, 10);
};

/** Exibe data da API (ISO / yyyy-mm-dd) como dd/mm/aaaa na lista. */
const formatDateBr = (value: string | null | undefined): string => {
  const iso = toInputDate(value);
  if (!iso || iso.length < 10) return '—';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d || y.length !== 4) return '—';
  return `${d}/${m}/${y}`;
};

const defaultDates = (): Pick<GoalFormState, 'startDate' | 'plannedEndDate'> => {
  const start = new Date();
  const planned = new Date();
  planned.setDate(planned.getDate() + 30);
  const fmt = (d: Date): string => d.toISOString().slice(0, 10);
  return { startDate: fmt(start), plannedEndDate: fmt(planned) };
};

const PersonalDevelopment: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [goals, setGoals] = useState<PersonalDevelopmentGoal[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [form, setForm] = useState<GoalFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [goalToDelete, setGoalToDelete] = useState<PersonalDevelopmentGoal | null>(null);
  const [deleteGoalInProgress, setDeleteGoalInProgress] = useState(false);
  const [materials, setMaterials] = useState<PersonalDevelopmentMaterial[]>([]);
  const [fgceDismissed, setFgceDismissed] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(true);
  const [expandedMaterialId, setExpandedMaterialId] = useState<string | null>(null);
  const [materialBlobUrls, setMaterialBlobUrls] = useState<Record<string, string>>({});
  const [loadingMaterialId, setLoadingMaterialId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<PersonalDevelopmentMaterial | null>(null);
  const [deleteMaterialInProgress, setDeleteMaterialInProgress] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const materialFileInputRef = useRef<HTMLInputElement>(null);

  const loadUsers = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<User[]>('/users');
      setUsers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadMaterials = useCallback(async (): Promise<void> => {
    setLoadingMaterials(true);
    try {
      const { data } = await api.get<
        | PersonalDevelopmentMaterial[]
        | { items: PersonalDevelopmentMaterial[]; fgceDismissed: boolean }
      >('/personal-development/materials');
      if (Array.isArray(data)) {
        setMaterials(data);
        setFgceDismissed(false);
      } else {
        setMaterials(data.items ?? []);
        setFgceDismissed(!!data.fgceDismissed);
      }
    } catch (e) {
      console.error(e);
      setMaterials([]);
      setFgceDismissed(false);
      setAlert({
        isOpen: true,
        message: 'Não foi possível carregar os materiais em PDF.',
        type: 'error',
      });
    } finally {
      setLoadingMaterials(false);
    }
  }, []);

  const loadGoals = useCallback(async (userId: string): Promise<void> => {
    if (!userId) {
      setGoals([]);
      return;
    }
    setLoadingGoals(true);
    try {
      const { data } = await api.get<PersonalDevelopmentGoal[]>(
        `/personal-development?userId=${encodeURIComponent(userId)}`,
      );
      setGoals(data);
    } catch (e) {
      console.error(e);
      setGoals([]);
      setAlert({ isOpen: true, message: 'Não foi possível carregar as competências.', type: 'error' });
    } finally {
      setLoadingGoals(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'hr') {
      void loadUsers();
      void loadMaterials();
    }
  }, [loadUsers, loadMaterials, user]);

  const materialBlobUrlsRef = useRef(materialBlobUrls);
  materialBlobUrlsRef.current = materialBlobUrls;

  useEffect(() => {
    return () => {
      Object.values(materialBlobUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      void loadGoals(selectedUserId);
    } else {
      setGoals([]);
    }
  }, [selectedUserId, loadGoals]);

  const resetFormToNew = useCallback((): void => {
    setEditingId(null);
    setForm({ ...emptyForm(), ...defaultDates() });
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      resetFormToNew();
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  }, [selectedUserId, resetFormToNew]);

  const collaborators = useMemo(
    () => users.filter((u) => u.active).sort((a, b) => a.name.localeCompare(b.name)),
    [users],
  );

  const competencySelectOptions = useMemo(() => {
    const base = PERSONAL_DEVELOPMENT_COMPETENCIES.map((c) => ({
      value: c.value,
      label: c.label,
    }));
    const extra: { value: string; label: string }[] = [];
    if (form.competencyTitle && !isPersonalDevelopmentCompetency(form.competencyTitle)) {
      extra.push({
        value: form.competencyTitle,
        label: `${form.competencyTitle} (cadastro anterior)`,
      });
    }
    return [{ value: '', label: 'Selecione a competência…' }, ...extra, ...base];
  }, [form.competencyTitle]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selectedUserId) {
      setAlert({ isOpen: true, message: 'Selecione um colaborador.', type: 'warning' });
      return;
    }
    if (!form.competencyTitle.trim()) {
      setAlert({ isOpen: true, message: 'Selecione a competência a ser desenvolvida.', type: 'warning' });
      return;
    }
    if (!form.startDate || !form.plannedEndDate) {
      setAlert({
        isOpen: true,
        message: 'Informe a data de início e a data prevista para finalizar.',
        type: 'warning',
      });
      return;
    }

    const body = {
      userId: selectedUserId,
      competencyTitle: form.competencyTitle.trim(),
      activityBook: form.activityBook.trim() || undefined,
      activityMovie: form.activityMovie.trim() || undefined,
      activityTask: form.activityTask.trim() || undefined,
      startDate: form.startDate,
      plannedEndDate: form.plannedEndDate,
      actualEndDate: form.actualEndDate.trim() || undefined,
      developed: form.developed,
    };

    try {
      if (editingId) {
        const { data } = await api.patch<PersonalDevelopmentGoal>(
          `/personal-development/${editingId}`,
          body,
        );
        setGoals((prev) => prev.map((g) => (g.id === data.id ? data : g)));
        setAlert({ isOpen: true, message: 'Registro atualizado.', type: 'success' });
      } else {
        const { data } = await api.post<PersonalDevelopmentGoal>('/personal-development', body);
        setGoals((prev) => [data, ...prev]);
        setAlert({ isOpen: true, message: 'Competência cadastrada.', type: 'success' });
      }
      resetFormToNew();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setAlert({
        isOpen: true,
        message: Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao salvar.',
        type: 'error',
      });
    }
  };

  const startEdit = (g: PersonalDevelopmentGoal): void => {
    setEditingId(g.id);
    setForm({
      competencyTitle: g.competencyTitle,
      activityBook: g.activityBook ?? '',
      activityMovie: g.activityMovie ?? '',
      activityTask: g.activityTask ?? '',
      startDate: toInputDate(g.startDate),
      plannedEndDate: toInputDate(g.plannedEndDate),
      actualEndDate: toInputDate(g.actualEndDate),
      developed: g.developed,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const requestDeleteGoal = (g: PersonalDevelopmentGoal): void => {
    setGoalToDelete(g);
  };

  const cancelDeleteGoal = (): void => {
    if (deleteGoalInProgress) return;
    setGoalToDelete(null);
  };

  const confirmDeleteGoal = async (): Promise<void> => {
    if (!goalToDelete) return;
    const g = goalToDelete;
    setDeleteGoalInProgress(true);
    try {
      await api.delete(`/personal-development/${g.id}`);
      setGoals((prev) => prev.filter((x) => x.id !== g.id));
      if (editingId === g.id) {
        resetFormToNew();
      }
      setGoalToDelete(null);
      setAlert({ isOpen: true, message: 'Registro removido.', type: 'success' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao remover.';
      setAlert({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setDeleteGoalInProgress(false);
    }
  };

  const ensureMaterialBlobUrl = useCallback(
    async (id: string): Promise<string> => {
      if (id === PUBLIC_FGCE_ID) return MATERIAL_FGCE_PUBLIC_URL;
      const existing = materialBlobUrls[id];
      if (existing) return existing;
      setLoadingMaterialId(id);
      try {
        const res = await api.get<Blob>(`/personal-development/materials/${id}/file`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(res.data);
        setMaterialBlobUrls((prev) => {
          if (prev[id]) {
            URL.revokeObjectURL(url);
            return prev;
          }
          return { ...prev, [id]: url };
        });
        return url;
      } finally {
        setLoadingMaterialId(null);
      }
    },
    [materialBlobUrls],
  );

  const displayMaterials = useMemo((): PersonalDevelopmentMaterial[] => {
    const hasFgce = materials.some(isFgceMaterial);
    if (hasFgce || fgceDismissed) return materials;
    return [publicFgceMaterial(), ...materials];
  }, [materials, fgceDismissed]);

  const toggleMaterialExpand = async (m: PersonalDevelopmentMaterial): Promise<void> => {
    if (expandedMaterialId === m.id) {
      setExpandedMaterialId(null);
      return;
    }
    try {
      await ensureMaterialBlobUrl(m.id);
    } catch {
      setAlert({
        isOpen: true,
        message: 'Não foi possível carregar este PDF.',
        type: 'error',
      });
      return;
    }
    setExpandedMaterialId(m.id);
  };

  const openMaterialInNewTab = async (m: PersonalDevelopmentMaterial): Promise<void> => {
    try {
      const url = await ensureMaterialBlobUrl(m.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setAlert({
        isOpen: true,
        message: 'Não foi possível abrir este PDF.',
        type: 'error',
      });
    }
  };
  const resetUploadForm = (): void => {
    setUploadTitle('');
    setUploadFile(null);
    setUploadOpen(false);
    if (materialFileInputRef.current) {
      materialFileInputRef.current.value = '';
    }
  };

  const handleUploadMaterial = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const title = uploadTitle.trim();
    if (title.length < 2) {
      setAlert({
        isOpen: true,
        message: 'Informe um nome para o PDF (mínimo 2 caracteres).',
        type: 'warning',
      });
      return;
    }
    if (!uploadFile) {
      setAlert({ isOpen: true, message: 'Selecione um arquivo PDF.', type: 'warning' });
      return;
    }
    if (
      uploadFile.type !== 'application/pdf' &&
      !uploadFile.name.toLowerCase().endsWith('.pdf')
    ) {
      setAlert({ isOpen: true, message: 'Apenas arquivos PDF são permitidos.', type: 'warning' });
      return;
    }

    const fd = new FormData();
    fd.append('title', title);
    fd.append('file', uploadFile);
    setUploading(true);
    try {
      const { data } = await api.post<PersonalDevelopmentMaterial>(
        '/personal-development/materials',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setMaterials((prev) => [...prev, data]);
      resetUploadForm();
      setExpandedMaterialId(data.id);
      setAlert({ isOpen: true, message: `PDF «${data.title}» adicionado.`, type: 'success' });
      try {
        await ensureMaterialBlobUrl(data.id);
      } catch {
        // Lista já atualizada; preview pode ser aberto depois.
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setAlert({
        isOpen: true,
        message: Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao enviar o PDF.',
        type: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const startRenameMaterial = (m: PersonalDevelopmentMaterial): void => {
    if (m.id === PUBLIC_FGCE_ID) {
      void (async () => {
        await loadMaterials();
        setAlert({
          isOpen: true,
          message: 'Material FGCE sincronizado. Clique em Renomear novamente.',
          type: 'success',
        });
      })();
      return;
    }
    setRenamingId(m.id);
    setRenameTitle(m.title);
  };

  const cancelRenameMaterial = (): void => {
    setRenamingId(null);
    setRenameTitle('');
  };

  const confirmRenameMaterial = async (): Promise<void> => {
    if (!renamingId) return;
    const title = renameTitle.trim();
    if (title.length < 2) {
      setAlert({
        isOpen: true,
        message: 'Informe um nome para o PDF (mínimo 2 caracteres).',
        type: 'warning',
      });
      return;
    }
    try {
      const { data } = await api.patch<PersonalDevelopmentMaterial>(
        `/personal-development/materials/${renamingId}`,
        { title },
      );
      setMaterials((prev) => prev.map((m) => (m.id === data.id ? data : m)));
      cancelRenameMaterial();
      setAlert({ isOpen: true, message: 'Nome do PDF atualizado.', type: 'success' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setAlert({
        isOpen: true,
        message: Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao renomear.',
        type: 'error',
      });
    }
  };

  const confirmDeleteMaterial = async (): Promise<void> => {
    if (!materialToDelete) return;
    const m = materialToDelete;
    setDeleteMaterialInProgress(true);
    try {
      if (m.id === PUBLIC_FGCE_ID || isFgceMaterial(m)) {
        await api.delete('/personal-development/materials/fgce');
        setFgceDismissed(true);
        setMaterials((prev) => prev.filter((x) => !isFgceMaterial(x)));
      } else {
        await api.delete(`/personal-development/materials/${m.id}`);
        setMaterials((prev) => prev.filter((x) => x.id !== m.id));
      }
      setMaterialBlobUrls((prev) => {
        const next = { ...prev };
        if (next[m.id]) {
          URL.revokeObjectURL(next[m.id]);
          delete next[m.id];
        }
        return next;
      });
      if (expandedMaterialId === m.id) setExpandedMaterialId(null);
      setMaterialToDelete(null);
      setAlert({ isOpen: true, message: `PDF «${m.title}» removido da lista.`, type: 'success' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao remover o PDF.';
      setAlert({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setDeleteMaterialInProgress(false);
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'hr')) {
    return (
      <PageContainer maxWidth="md">
        <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography>Acesso restrito a administradores ou RH.</Typography>
        </Paper>
      </PageContainer>
    );
  }

  if (loadingUsers) {
    return (
      <PageContainer maxWidth="md">
        <Typography color="text.secondary">Carregando…</Typography>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      icon={<TrendingUpOutlinedIcon />}
      title="Competências e atividades por colaborador"
      subtitle="Cadastre competências a desenvolver, atividades (livro, filme, tarefa), prazos e acompanhe a conclusão."
    >
      <Stack spacing={4}>
        {/* ---- Materiais de consulta (PDF) ---- */}
        <Paper
          component="section"
          aria-labelledby="pd-material-heading"
          sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
          >
            <Typography id="pd-material-heading" variant="h6" component="h2" sx={{ fontWeight: 800 }}>
              Materiais de consulta (PDF)
            </Typography>
            <Button
              variant={uploadOpen ? 'outlined' : 'contained'}
              color="primary"
              startIcon={uploadOpen ? <CloseIcon /> : <AddIcon />}
              onClick={() => {
                if (uploadOpen) resetUploadForm();
                else setUploadOpen(true);
              }}
              aria-expanded={uploadOpen}
            >
              {uploadOpen ? 'Cancelar' : 'Adicionar PDF'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Adicione os documentos de referência. Cada PDF aparece com o nome informado e pode ser
            expandido, aberto, renomeado ou removido.
          </Typography>

          {uploadOpen ? (
            <Box
              component="form"
              onSubmit={handleUploadMaterial}
              sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, backgroundColor: 'action.hover' }}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    id="pd-material-title"
                    label="Nome do PDF"
                    fullWidth
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Ex.: Guia de competências, Manual FGCE…"
                    required
                    disabled={uploading}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    id="pd-material-file-label"
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.85rem', mb: '6px' }}
                  >
                    Arquivo PDF
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    role="group"
                    aria-labelledby="pd-material-file-label"
                    sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
                  >
                    <input
                      ref={materialFileInputRef}
                      id="pd-material-file"
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                      style={{ display: 'none' }}
                      tabIndex={-1}
                      aria-hidden="true"
                      disabled={uploading}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      aria-label="Escolher arquivo PDF"
                      onClick={() => materialFileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      Escolher arquivo
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      title={uploadFile ? uploadFile.name : 'Nenhum arquivo selecionado'}
                      aria-live="polite"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {uploadFile ? uploadFile.name : 'Nenhum arquivo selecionado'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained" color="primary" disabled={uploading} startIcon={<AddIcon />}>
                  {uploading ? 'Enviando…' : 'Salvar PDF'}
                </Button>
              </Box>
            </Box>
          ) : null}

          {loadingMaterials ? (
            <Typography variant="body2" color="text.secondary" role="status" sx={{ mt: 2 }}>
              Carregando materiais…
            </Typography>
          ) : displayMaterials.length === 0 ? (
            <Typography variant="body2" color="text.secondary" role="status" sx={{ mt: 2 }}>
              Nenhum PDF cadastrado. Use «Adicionar PDF» para enviar o primeiro material.
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {displayMaterials.map((m) => {
                const isExpanded = expandedMaterialId === m.id;
                const isLoading = loadingMaterialId === m.id;
                const previewUrl =
                  m.id === PUBLIC_FGCE_ID
                    ? MATERIAL_FGCE_PUBLIC_URL
                    : materialBlobUrls[m.id];
                const isRenaming = renamingId === m.id;

                return (
                  <Paper
                    key={m.id}
                    variant="outlined"
                    sx={{ border: '1px solid', borderColor: 'divider', p: { xs: 1.5, sm: 2 } }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' } }}
                    >
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
                        <StatusChip label="PDF" tone="accent" />
                        {isRenaming ? (
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{ flex: 1, alignItems: { xs: 'stretch', sm: 'center' } }}
                          >
                            <TextField
                              value={renameTitle}
                              onChange={(e) => setRenameTitle(e.target.value)}
                              size="small"
                              fullWidth
                              aria-label="Novo nome do PDF"
                              slotProps={{ htmlInput: { maxLength: 200 } }}
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => void confirmRenameMaterial()}
                            >
                              Salvar
                            </Button>
                            <Button variant="outlined" size="small" onClick={cancelRenameMaterial}>
                              Cancelar
                            </Button>
                          </Stack>
                        ) : (
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {m.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {m.originalName}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}
                      >
                        <Tooltip title={isExpanded ? 'Recolher' : 'Expandir'}>
                          <span>
                            <IconButton
                              onClick={() => void toggleMaterialExpand(m)}
                              aria-expanded={isExpanded}
                              disabled={isLoading}
                              aria-label={isExpanded ? 'Recolher' : 'Expandir'}
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Abrir em nova aba">
                          <span>
                            <IconButton
                              onClick={() => void openMaterialInNewTab(m)}
                              disabled={isLoading}
                              aria-label="Abrir em nova aba"
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {!isRenaming ? (
                          <>
                            <Tooltip title="Renomear">
                              <IconButton onClick={() => startRenameMaterial(m)} aria-label="Renomear">
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remover">
                              <IconButton
                                color="error"
                                onClick={() => setMaterialToDelete(m)}
                                aria-label="Remover"
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null}
                      </Stack>
                    </Stack>
                    {isExpanded && previewUrl ? (
                      <Box id={`pd-material-viewer-${m.id}`} sx={{ mt: 2 }}>
                        <Box
                          component="iframe"
                          title={`${m.title} — visualizador de PDF`}
                          src={previewUrl}
                          sx={{
                            width: '100%',
                            height: { xs: 360, sm: 520 },
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </Box>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* ---- Colaborador + formulário + lista de competências ---- */}
        <Paper
          component="section"
          sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}
        >
          <PremiumSelect
            label="Colaborador"
            value={selectedUserId}
            options={[
              { value: '', label: 'Selecione…' },
              ...collaborators.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` })),
            ]}
            onChange={(v) => setSelectedUserId(v)}
          />
          {!selectedUserId ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Escolha um colaborador para ver ou cadastrar competências.
            </Typography>
          ) : (
            <>
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
                  {editingId ? 'Editar competência' : 'Nova competência'}
                </Typography>

                <PremiumSelect
                  label="Competência a ser desenvolvida"
                  value={form.competencyTitle}
                  options={competencySelectOptions}
                  onChange={(v) => setForm({ ...form, competencyTitle: v })}
                  required
                />

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Atividades
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    id="pd-book"
                    label="Ler um livro"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.activityBook}
                    onChange={(e) => setForm({ ...form, activityBook: e.target.value })}
                    placeholder="Título, autor ou o que será lido"
                  />
                  <TextField
                    id="pd-movie"
                    label="Assistir um filme"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.activityMovie}
                    onChange={(e) => setForm({ ...form, activityMovie: e.target.value })}
                    placeholder="Título ou tema do filme"
                  />
                  <TextField
                    id="pd-task"
                    label="Executar uma tarefa"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.activityTask}
                    onChange={(e) => setForm({ ...form, activityTask: e.target.value })}
                    placeholder="Descrição da tarefa prática"
                  />
                </Stack>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      id="pd-start"
                      label="Data de início *"
                      type="date"
                      fullWidth
                      value={form.startDate}
                      onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      id="pd-planned"
                      label="Data prevista para finalizar *"
                      type="date"
                      fullWidth
                      value={form.plannedEndDate}
                      onChange={(e) => setForm({ ...form, plannedEndDate: e.target.value })}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      id="pd-actual"
                      label="Data real de finalização"
                      type="date"
                      fullWidth
                      value={form.actualEndDate}
                      onChange={(e) => setForm({ ...form, actualEndDate: e.target.value })}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                </Grid>

                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Checkbox
                      id="pd-developed"
                      checked={form.developed}
                      onChange={(e) => setForm({ ...form, developed: e.target.checked })}
                    />
                  }
                  label="Competência desenvolvida (concluída)"
                />

                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mt: 2, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}
                >
                  {editingId ? (
                    <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => resetFormToNew()}>
                      Cancelar edição
                    </Button>
                  ) : null}
                  <Button type="submit" variant="contained" color="primary" startIcon={editingId ? <SaveIcon /> : <AddIcon />}>
                    {editingId ? 'Salvar alterações' : 'Cadastrar competência'}
                  </Button>
                </Stack>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
                Competências cadastradas
              </Typography>
              {loadingGoals ? (
                <Typography variant="body2" color="text.secondary">
                  Carregando lista…
                </Typography>
              ) : goals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma competência cadastrada para este colaborador.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {goals.map((g) => (
                    <Paper
                      key={g.id}
                      component="article"
                      variant="outlined"
                      sx={{ border: '1px solid', borderColor: 'divider', p: { xs: 2, sm: 2.5 } }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {g.competencyTitle}
                        </Typography>
                        <StatusChip
                          label={g.developed ? 'Desenvolvida' : 'Em desenvolvimento'}
                          tone={g.developed ? 'success' : 'warning'}
                        />
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 1, flexWrap: 'wrap', gap: 1, color: 'text.secondary' }}
                      >
                        <Typography variant="body2">
                          <Box component="strong" sx={{ color: 'text.primary' }}>
                            Início:
                          </Box>{' '}
                          {formatDateBr(g.startDate)}
                        </Typography>
                        <Typography variant="body2">
                          <Box component="strong" sx={{ color: 'text.primary' }}>
                            Prevista:
                          </Box>{' '}
                          {formatDateBr(g.plannedEndDate)}
                        </Typography>
                        <Typography variant="body2">
                          <Box component="strong" sx={{ color: 'text.primary' }}>
                            Real:
                          </Box>{' '}
                          {formatDateBr(g.actualEndDate)}
                        </Typography>
                      </Stack>
                      {g.activityBook ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <Box component="strong">Livro:</Box> {g.activityBook}
                        </Typography>
                      ) : null}
                      {g.activityMovie ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <Box component="strong">Filme:</Box> {g.activityMovie}
                        </Typography>
                      ) : null}
                      {g.activityTask ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <Box component="strong">Tarefa:</Box> {g.activityTask}
                        </Typography>
                      ) : null}
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 2, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => startEdit(g)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => requestDeleteGoal(g)}
                        >
                          Excluir
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Paper>
      </Stack>

      <ConfirmModal
        isOpen={goalToDelete !== null}
        title="Remover competência"
        message={
          goalToDelete
            ? `Remover a competência «${goalToDelete.competencyTitle}» deste colaborador? Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel={deleteGoalInProgress ? 'Removendo…' : 'Remover'}
        cancelLabel="Cancelar"
        onConfirm={() => void confirmDeleteGoal()}
        onCancel={cancelDeleteGoal}
        confirmDisabled={deleteGoalInProgress}
        tone="danger"
      />

      <ConfirmModal
        isOpen={materialToDelete !== null}
        title="Remover PDF"
        message={
          materialToDelete
            ? `Remover o PDF «${materialToDelete.title}»? Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel={deleteMaterialInProgress ? 'Removendo…' : 'Remover'}
        cancelLabel="Cancelar"
        onConfirm={() => void confirmDeleteMaterial()}
        onCancel={() => {
          if (!deleteMaterialInProgress) setMaterialToDelete(null);
        }}
        confirmDisabled={deleteMaterialInProgress}
        tone="danger"
      />

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </PageContainer>
  );
};

export default PersonalDevelopment;
